import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { Button, useTheme } from '@mui/material';
import { ArrowIcon } from '@sharedCR/coreDesignSystem/icons';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection } from 'constants/coreDesignSystem';
import { checkIsLightTheme } from 'utils/theme';

import {
  selectAvailableTabsArray,
  selectEditingIds,
  selectIsTabsNavigationDisabled,
} from 'store/loanApplications/selectors';

import { GREY_DARK, GREY_LIGHT } from './BottomBtnGroup.constants';
import { LoanTabLayoutTestIds } from './LoanTabLayout.testIds';

const getButtonSx = ({ palette }) => {
  const isLightTheme = checkIsLightTheme(palette.type);

  return {
    border: `1px solid ${palette.common[isLightTheme ? 'black' : 'white']}`,
    backgroundColor: palette.grey[isLightTheme ? GREY_LIGHT : GREY_DARK],
  };
};

const BottomBtnGroup = ({ actionItem = null, showNavigationButtons = false }) => {
  const theme = useTheme();
  const buttonSx = getButtonSx(theme);
  const { applicationId, tab: loanEditorTab } = useParams();

  const availableTabs = useSelector(selectAvailableTabsArray);
  const editingIds = useSelector(selectEditingIds);
  const isTabsNavigationDisabled = useSelector(selectIsTabsNavigationDisabled);

  const tabIndex = availableTabs.findIndex(tab => tab === loanEditorTab);

  const prevTabIndex = tabIndex - 1 < 0 ? null : tabIndex - 1;
  const nextTabIndex = tabIndex + 1 > availableTabs.length - 1 ? null : tabIndex + 1;

  if (editingIds.length) {
    return null;
  }

  return (
    <div className="flex mt-20">
      <div className="flex grow gap-x-24">{actionItem}</div>

      {showNavigationButtons && (
        <>
          {_.isNumber(prevTabIndex) && (
            <Button
              className="rounded-full capitalize mr-12"
              color="default"
              component={Link}
              data-tab-index={prevTabIndex}
              data-testid={LoanTabLayoutTestIds.PREVIOUS_STEP_BUTTON}
              disabled={isTabsNavigationDisabled}
              role="button"
              sx={buttonSx}
              to={LoansPaths.applicationManagement.generatePath({
                [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
                [LOANS_ROUTING_PARAMS.tab]: availableTabs[prevTabIndex],
              })}
              type="button"
            >
              <ArrowIcon direction={IconDirection.LEFT} />
              <span className="hidden md:inline">Previous Step</span>
            </Button>
          )}

          {_.isNumber(nextTabIndex) && (
            <Button
              className="rounded-full capitalize"
              color="default"
              component={Link}
              data-tab-index={nextTabIndex}
              data-testid={LoanTabLayoutTestIds.NEXT_STEP_BUTTON}
              disabled={isTabsNavigationDisabled}
              role="button"
              sx={buttonSx}
              to={LoansPaths.applicationManagement.generatePath({
                [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
                [LOANS_ROUTING_PARAMS.tab]: availableTabs[nextTabIndex],
              })}
              type="button"
            >
              <span className="hidden md:inline">Next Step</span>
              <ArrowIcon direction={IconDirection.RIGHT} />
            </Button>
          )}
        </>
      )}
    </div>
  );
};

BottomBtnGroup.propTypes = {
  actionItem: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
  showNavigationButtons: PropTypes.bool,
};

export default memo(BottomBtnGroup);
